import React from 'react'

import {
  ClockInWidgetDocument,
  ClockInWidgetEmployeesSettingFragment,
  ClockInWidgetOpenShiftDocument,
  ClockInWidgetPoliciesFragment,
  TimesheetBreakDurationRuleFragment,
  TimesheetBreakSettingFragment,
  TimesheetEstimatedTimeFragment,
  TimesheetLastWorkingShiftDocument,
  TimesheetOpenShiftFragment,
  TimesheetOvertimeRequestDocument,
  TimesheetOvertimeRequestFragment,
  TimesheetPageLastWorkingShiftFragment,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import moment from 'moment'
import { DeepPartial } from 'utility-types'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { dateToString, stringToDate } from 'modules/core/lib/dates'

import { AttendanceBreakConfigurations } from 'modules/attendance/hooks/useGetAttendanceBreakConfigurationsRange'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'
import EstimatedTimesUtils from 'modules/attendance/lib/utils/EstimatedTimes'
import EstimatedTimes from 'modules/attendance/lib/utils/EstimatedTimes'

type AttendanceOpenShift = TimesheetOpenShiftFragment

const DATA_REFRESH_INTERVAL = [1, 'hour']

export interface Return {
  today: moment.Moment
  setToday: (date: string | null) => void
  loading: boolean
  lastWorkingShift?: TimesheetPageLastWorkingShiftFragment | null
  openShift?: DeepPartial<AttendanceOpenShift> | null
  onRefresh: () => void
  setLoading: (loading: boolean) => void
  breakSetting: TimesheetBreakSettingFragment | null
  checkRefreshData: () => void
  attendanceSetting: ClockInWidgetEmployeesSettingFragment
  overtimeRequest?: TimesheetOvertimeRequestFragment | null
  breakDurationRules: TimesheetBreakDurationRuleFragment[]
  todayShifts: TimesheetPageShiftFragment[]
  estimatedTime: TimesheetEstimatedTimeFragment
  policies?: ClockInWidgetPoliciesFragment | null
  canReadOwnedAttendance: boolean
  breaksEnabled: boolean
  desktopClockIn: boolean
  mobileClockIn: boolean
  canSeeOwnedAttendance: boolean
  breakConfigurations: AttendanceBreakConfigurations[]
  breakTypes: {
    id: string
    name: string
    isPaid: boolean
  }[]
}

const useData = (): Return => {
  const { employee } = useCurrentSession()
  const now = moment()

  const attendanceEmployeesPolicyId = employee?.attendanceEmployeesPolicyId ?? -1

  const fallbackToday = dateToString(now, 'backend')

  const [loading, setLoading] = React.useState(true)
  const [today, setToday] = React.useState<string | null>(null)

  const [fetchDataTime, setFetchDataTime] = React.useState<moment.Moment>(now)

  const [fetchClockInWidgetData, { data: clockInData }] = useLazyQuery(ClockInWidgetDocument, {
    variables: {
      employeeId: Number(employee?.id),
      date: today || fallbackToday,
      attendanceEmployeesPolicyId,
      skipBreakConfigurationsForDates: attendanceEmployeesPolicyId === -1,
    },
    skip: !employee?.id,
  })

  const [
    fetchOpenShift,
    { called: calledOpenShift, loading: loadingOpenShift, data: openShiftData },
  ] = useLazyQuery(ClockInWidgetOpenShiftDocument, {
    variables: {
      employeeId: Number(employee?.id),
    },
    skip: !employee?.id,
    onCompleted: (data) => {
      const completedOpenShift = data?.attendance?.employee?.openShift
      const _today = completedOpenShift
        ? completedOpenShift.date
        : dateToString(moment().toDate(), 'backend')

      setToday(_today)
    },
  })

  const openShift = openShiftData?.attendance?.employee?.openShift
  const employeeClockInData = clockInData?.attendance?.employee
  const canReadOwnedAttendance = !!openShiftData?.attendance._policies.companySeeOwned.can
  const todayShifts = AttendanceShiftUtils.currentShiftsForDate(
    stringToDate(today || fallbackToday, 'backend'),
    employeeClockInData?.attendanceShiftsConnection.nodes ?? []
  )
  const attendanceSetting =
    clockInData?.attendance?.employeesSettingsConnection?.nodes[0] ??
    EmployeesSettingsUtils.defaultEmployeesSetting<ClockInWidgetEmployeesSettingFragment>()

  const breakSetting =
    clockInData?.attendance?.employeesSettingsConnection?.nodes[0]?.attendanceBreakSetting ?? null

  const breakDurationRules = breakSetting?.attendanceBreakDurationRules ?? []

  const desktopClockIn = !!attendanceSetting?.desktopClockin
  const mobileClockIn = !!attendanceSetting?.mobileClockin

  const estimatedTimes = employeeClockInData?.attendanceEstimatedTimes ?? []
  const estimatedTimesByDates =
    EstimatedTimesUtils.estimatedTimesByDates<TimesheetEstimatedTimeFragment>(estimatedTimes)
  const estimatedTime =
    estimatedTimesByDates[today || fallbackToday] ??
    EstimatedTimes.empty<TimesheetEstimatedTimeFragment>(today || fallbackToday)

  const policies = employeeClockInData?._policies ?? null

  // FIXME: This comes from the getCurrentSession query, but it should be the dashboard page query
  const { id: employeeId } = employee ?? {}

  const [fetchLastWorkingShift, { data: lastWorkingShiftData }] = useLazyQuery(
    TimesheetLastWorkingShiftDocument,
    {
      variables: {
        employeeId: employeeId ?? 0,
      },
    }
  )

  const [fetchOvertimeRequest, { data: overtimeRequestData }] = useLazyQuery(
    TimesheetOvertimeRequestDocument,
    {
      variables: {
        employeeIds: [Number(employee?.id)],
        startOn: today || fallbackToday,
        endOn: today || fallbackToday,
      },
      skip: !employee?.id,
    }
  )

  const lastWorkingShift = lastWorkingShiftData?.attendance?.shiftsConnection?.nodes?.[0]
  const overtimeRequest = overtimeRequestData?.attendance?.overtimeRequests?.[0] ?? undefined

  const fetchData = async () => {
    if (today) {
      await Promise.all([
        fetchClockInWidgetData({
          variables: { employeeId: Number(employee?.id), date: today, attendanceEmployeesPolicyId },
        }),
        fetchLastWorkingShift(),
        fetchOvertimeRequest({
          variables: {
            employeeIds: [Number(employee?.id)],
            startOn: today,
            endOn: today,
          },
        }),
      ])
    }

    if (!employee) return Promise.resolve()
  }

  const onRefresh = async () => {
    setLoading(true)
    await fetchData()
    setFetchDataTime(now)
    setLoading(false)
  }

  const checkRefreshData = () => {
    if (document.visibilityState === 'hidden') return

    const interval = now.subtract(...DATA_REFRESH_INTERVAL)

    if (!fetchDataTime.isBefore(interval)) return

    setToday(null)
  }

  useEffect(() => {
    async function loadDependencies() {
      if (today == null) {
        fetchOpenShift()
      }

      if (calledOpenShift && !loadingOpenShift && today) {
        onRefresh()
      }
    }
    loadDependencies()
  }, [today]) // eslint-disable-line react-hooks/exhaustive-deps

  const todayTime = today ? stringToDate(today, 'backend') : moment()

  const breaksEnabled = !!attendanceSetting?.enableBreaks

  const canSeeOwnedAttendance = !!clockInData?.attendance._policies.companySeeOwned.can

  const attendanceBreakConfigurationsRange =
    clockInData?.attendance?.breakConfigurationsForDatesConnection?.nodes || []

  const breakConfigurationsRange = attendanceBreakConfigurationsRange.reduce((acc, item) => {
    acc.set(item.date, item.breakConfigurations)
    return acc
  }, new Map<string, (typeof attendanceBreakConfigurationsRange)[number]['breakConfigurations']>())

  const breakConfigurations = breakConfigurationsRange.get(moment().format('YYYY-MM-DD')) ?? []

  const breakTypes = breakConfigurations.map((config) => ({
    id: `${config.timeSettingsBreakConfigurationId}`,
    name: config.name,
    isPaid: !!config.paid,
  }))

  return {
    today: todayTime,
    setToday,
    loading,
    openShift,
    lastWorkingShift,
    onRefresh,
    setLoading,
    breakSetting,
    checkRefreshData,
    attendanceSetting,
    overtimeRequest,
    breaksEnabled,
    breakDurationRules,
    todayShifts,
    estimatedTime,
    policies,
    canReadOwnedAttendance,
    desktopClockIn,
    mobileClockIn,
    canSeeOwnedAttendance,
    breakConfigurations,
    breakTypes,
  }
}

export default useData
